import React from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  Autocomplete,
  Icon,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { AppDatePicker } from "../../../../components/AppDatePicker";
import { Batch } from "../../types/inventory.types";
import {
  formatUnitValue,
  convertUnit,
  getUnitOptions,
} from "../../utils/unit-measurements";
import { SelectChangeEvent } from "@mui/material/Select";
import { toast } from "react-toastify";
import { supplierService } from "../../services/supplier.service";
import { facilitiesService } from "../../services/facilities.service";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";

interface BatchManagerProps {
  batches: Batch[];
  unit: string;
  onBatchAdd: (batch: Batch) => void;
  onBatchDelete: (batchId: string) => void;
  onBatchUpdate: (batch: Batch) => void;
  currency?: string;
}

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  "& .MuiTableCell-head": {
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    padding: theme.spacing(2),
  },
  "& .MuiTableCell-body": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    padding: theme.spacing(2),
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    transition: theme.transitions.create(["box-shadow", "border-color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(
      ["background-color", "box-shadow", "border-color"],
      {
        duration: theme.transitions.duration.short,
      }
    ),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  "& .MuiInputLabel-root, & .MuiInputBase-input": {
    fontFamily: "Montserrat",
  },
  "& .MuiInputAdornment-root .MuiIcon-root": {
    color: theme.palette.primary.main,
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: 0,
  "& .MuiTypography-root": {
    fontSize: "1.25rem",
    fontWeight: 600,
    fontFamily: "Montserrat",
    color: theme.palette.text.primary,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  gap: theme.spacing(1),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  fontFamily: "Montserrat",
  "& .MuiIcon-root": {
    fontSize: 20,
    color: theme.palette.primary.main,
  },
}));

export const BatchManager: React.FC<BatchManagerProps> = ({
  batches,
  unit,
  onBatchAdd,
  onBatchDelete,
  onBatchUpdate,
  currency,
}) => {
  const theme = useTheme();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [selectedBatch, setSelectedBatch] = React.useState<Batch | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [suppliers, setSuppliers] = React.useState<string[]>([]);
  const [facilities, setFacilities] = React.useState<{
    branches: any[];
    warehouses: any[];
  }>({
    branches: [],
    warehouses: [],
  });
  const [useAutoBatchNumber, setUseAutoBatchNumber] = React.useState(true);
  const [costInputMode, setCostInputMode] = React.useState<"total" | "perUnit">(
    "total"
  );
  const [isLoadingFacilities, setIsLoadingFacilities] = React.useState(false);

  const generateBatchId = () => {
    const timestamp = new Date().getTime().toString(36);
    const random = Math.random().toString(36).substring(2, 5);
    return `BATCH-${timestamp}-${random}`.toUpperCase();
  };

  const [batchData, setBatchData] = React.useState<Batch>({
    batch_number: generateBatchId(),
    quantity: null,
    remaining_quantity: null,
    unit: unit,
    cost_per_unit: null,
    total_cost: null,
    expiry_date: new Date().getTime(),
    receive_date: new Date().getTime(),
    source_type: "supplier",
    source_name: "",
    brand_name: "",
    warehouse_branch: "",
    storage_place: "",
    notes: "",
  });

  // Load suppliers and facilities on component mount
  React.useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingFacilities(true);
        const [suppliersResponse, branchesResponse, warehousesResponse] =
          await Promise.all([
            supplierService.listSuppliers({}),
            facilitiesService.listBranches({}),
            facilitiesService.listWarehouses({}),
          ]);

        setSuppliers(
          suppliersResponse.data.map((supplier: any) => supplier.name)
        );
        setFacilities({
          branches: branchesResponse.data,
          warehouses: warehousesResponse.data,
        });
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load suppliers and facilities");
      } finally {
        setIsLoadingFacilities(false);
      }
    };
    loadData();
  }, []);

  // Update all batches when unit changes
  React.useEffect(() => {
    if (batches.length > 0 && unit) {
      batches.forEach((batch) => {
        if (batch.unit !== unit) {
          const convertedQuantity = convertUnit(
            batch.quantity || 0,
            batch.unit,
            unit
          );
          const convertedRemainingQuantity = convertUnit(
            batch.remaining_quantity || 0,
            batch.unit,
            unit
          );
          const updatedBatch = {
            ...batch,
            unit: unit,
            quantity: Number(convertedQuantity.toFixed(3)),
            remaining_quantity: Number(convertedRemainingQuantity.toFixed(3)),
            // Recalculate costs based on new unit
            cost_per_unit: batch.total_cost
              ? Number((batch.total_cost / convertedQuantity).toFixed(3))
              : batch.cost_per_unit,
          };
          onBatchUpdate(updatedBatch);
        }
      });
    }
  }, [unit, batches]);

  // Update current batch data unit when unit prop changes
  React.useEffect(() => {
    setBatchData((prev) => ({
      ...prev,
      unit: unit,
    }));
  }, [unit]);

  const handleDialogOpen = (batch?: Batch) => {
    if (batch) {
      setSelectedBatch(batch);
      setBatchData(batch);
      setUseAutoBatchNumber(false);
    } else {
      setSelectedBatch(null);
      setBatchData({
        batch_number: generateBatchId(),
        quantity: null,
        remaining_quantity: null,
        unit: unit,
        cost_per_unit: null,
        total_cost: null,
        expiry_date: new Date().getTime(),
        receive_date: new Date().getTime(),
        source_type: "supplier",
        source_name: "",
        brand_name: "",
        warehouse_branch: "",
        storage_place: "",
        notes: "",
      });
      setUseAutoBatchNumber(true);
    }
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedBatch(null);
    setUseAutoBatchNumber(true);
    setCostInputMode("total");
  };

  const handleChange =
    (field: keyof Batch) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setBatchData((prev) => {
        // Handle numeric fields
        if (
          [
            "quantity",
            "remaining_quantity",
            "cost_per_unit",
            "total_cost",
          ].includes(field)
        ) {
          const newData = {
            ...prev,
            [field]: value === "" ? null : Number(parseFloat(value).toFixed(3)),
          };

          // Handle cost calculations
          const quantity = Number(newData.quantity) || 0;

          if (field === "quantity") {
            const costPerUnit = Number(newData.cost_per_unit) || 0;
            newData.total_cost = Number((quantity * costPerUnit).toFixed(3));
          } else if (field === "total_cost") {
            const totalCost = Number(newData.total_cost) || 0;
            newData.cost_per_unit =
              quantity > 0 ? Number((totalCost / quantity).toFixed(3)) : 0;
          } else if (field === "cost_per_unit") {
            const costPerUnit = Number(newData.cost_per_unit) || 0;
            newData.total_cost = Number((quantity * costPerUnit).toFixed(3));
          }

          return newData;
        }

        // Handle string fields
        return {
          ...prev,
          [field]: value,
        };
      });
    };

  const handleSourceTypeChange = (event: SelectChangeEvent) => {
    setBatchData((prev) => ({
      ...prev,
      source_type: event.target.value as "supplier" | "transfer",
      source_name: "",
      warehouse_branch: "",
    }));
  };

  const handleResetBatchNumber = () => {
    setBatchData((prev) => ({
      ...prev,
      batch_number: generateBatchId(),
    }));
    setUseAutoBatchNumber(true);
  };

  const getFacilityOptions = () => {
    const options = [];

    for (const branch of facilities.branches) {
      options.push({
        id: branch.id,
        name: branch.name,
        type: "branch",
        label: branch.name,
        details: "Branch",
      });
    }

    for (const warehouse of facilities.warehouses) {
      options.push({
        id: warehouse._id,
        name: warehouse.name,
        type: "warehouse",
        label: warehouse.name,
        details: "Warehouse",
      });
    }

    return options;
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (selectedBatch) {
        // For updating existing batch
        console.log("selectedBatch");
        console.log(selectedBatch);
        const updatedBatch = {
          ...batchData,
          remaining_quantity: batchData.quantity, // Set remaining quantity equal to quantity
        };
        onBatchUpdate(updatedBatch);
      } else {
        // For new batch
        const batchToAdd = {
          ...batchData,
          remaining_quantity: batchData.quantity, // Set remaining quantity equal to quantity
        };
        await onBatchAdd(batchToAdd);
      }
      handleDialogClose();
    } catch (error) {
      console.error("Error saving batch:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalQuantity = () => {
    return batches?.reduce((total, batch) => {
      const convertedQuantity = convertUnit(
        batch.quantity || 0,
        batch.unit,
        unit
      );
      return total + convertedQuantity;
    }, 0);
  };

  const calculateAverageCost = () => {
    const totalCost = batches?.reduce(
      (sum, batch) => sum + (batch.total_cost || 0),
      0
    );
    const totalQuantity = calculateTotalQuantity();
    return totalQuantity > 0 ? totalCost / totalQuantity : 0;
  };
  console.log("Batches");
  console.log(batches);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{
            fontFamily: "Montserrat",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Icon fontSize="small">inventory_2</Icon>
          Manage stock batches and track inventory by batch
        </Typography>
        <Button
          variant="contained"
          onClick={() => handleDialogOpen()}
          startIcon={<Icon>add</Icon>}
          sx={{
            bgcolor: "#0891B2",
            fontFamily: "Montserrat",
            fontWeight: 600,
            boxShadow: (theme) =>
              `0 0 2px ${alpha(
                theme.palette.primary.main,
                0.2
              )}, 0 8px 16px -4px ${alpha(theme.palette.primary.dark, 0.2)}`,
            "&:hover": {
              bgcolor: "#0E7490",
              transform: "translateY(-1px)",
            },
          }}
        >
          Add New Batch
        </Button>
      </Box>

      <StyledPaper sx={{ mb: 3 }}>
        <Box sx={{ p: 3, display: "flex", gap: 6 }}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: "Montserrat",
              }}
            >
              <Icon fontSize="small">inventory_2</Icon>
              Total Quantity
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#0891B2",
                fontFamily: "Montserrat",
                fontWeight: 600,
              }}
            >
              {formatUnitValue(calculateTotalQuantity(), unit)}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: "Montserrat",
              }}
            >
              <Icon fontSize="small">payments</Icon>
              Average Cost
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#0891B2",
                fontFamily: "Montserrat",
                fontWeight: 600,
              }}
            >
              {currency || "$"}
              {calculateAverageCost().toFixed(3)} / {unit}
            </Typography>
          </Box>
        </Box>
      </StyledPaper>

      <StyledPaper>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Batch Number</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Cost per Unit</TableCell>
                <TableCell>Total Cost</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch) => {
                console.log("batch");
                console.log(batch);
                return (
                  <TableRow key={` ${batch.batch_number}`}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon fontSize="small" sx={{ color: "#0891B2" }}>
                          inventory_2
                        </Icon>
                        {batch.batch_number}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatUnitValue(batch.quantity || 0, batch.unit)}
                    </TableCell>
                    <TableCell>
                      {currency || "$"}
                      {(batch.cost_per_unit || 0).toFixed(3)}
                    </TableCell>
                    <TableCell>
                      {currency || "$"}
                      {(batch.total_cost || 0).toFixed(3)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Icon fontSize="small" sx={{ color: "action.active" }}>
                          event
                        </Icon>
                        {new Date(batch.expiry_date).toLocaleDateString()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDialogOpen(batch)}
                          sx={{
                            width: 34,
                            height: 34,
                            color: "#0891B2",
                            bgcolor: (theme) => alpha("#0891B2", 0.08),
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              bgcolor: (theme) => alpha("#0891B2", 0.12),
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                            },
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }}>edit</Icon>
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            onBatchDelete(batch.id || batch.batch_number)
                          }
                          sx={{
                            width: 34,
                            height: 34,
                            color: "#DC2626",
                            bgcolor: (theme) => alpha("#DC2626", 0.08),
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              bgcolor: (theme) => alpha("#DC2626", 0.12),
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                            },
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }}>delete</Icon>
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        color: "text.secondary",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 48,
                          color: "#0891B2",
                          opacity: 0.5,
                        }}
                      >
                        inventory
                      </Icon>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            fontFamily: "Montserrat",
                            fontWeight: 600,
                          }}
                        >
                          No batches added yet
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: "Montserrat" }}
                        >
                          Click "Add Batch" to start managing your inventory
                          batches
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={() => handleDialogOpen()}
                        startIcon={<Icon>add</Icon>}
                        sx={{
                          mt: 2,
                          borderColor: "#0891B2",
                          color: "#0891B2",
                          fontFamily: "Montserrat",
                          fontWeight: 600,
                          "&:hover": {
                            borderColor: "#0E7490",
                            bgcolor: alpha("#0891B2", 0.04),
                          },
                        }}
                      >
                        Add First Batch
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledPaper>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            borderRadius: 2,
          },
        }}
      >
        <StyledDialogTitle>
          {selectedBatch ? "Edit Batch" : "Add New Batch"}
        </StyledDialogTitle>
        <StyledDialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Source Information Section */}
            <Grid item xs={12}>
              <SectionTitle variant="subtitle2">
                <Icon>info</Icon>
                Source Information
              </SectionTitle>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Source Type</InputLabel>
                <Select
                  value={batchData.source_type}
                  onChange={handleSourceTypeChange}
                  label="Source Type"
                >
                  <MenuItem value="supplier">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon fontSize="small">business</Icon>
                      Supplier
                    </Box>
                  </MenuItem>
                  <MenuItem value="transfer">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon fontSize="small">swap_horiz</Icon>
                      Transfer
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {batchData.source_type === "supplier" ? (
                <Autocomplete
                  freeSolo
                  options={suppliers}
                  value={batchData.source_name}
                  onInputChange={(_, value) => {
                    setBatchData((prev) => ({ ...prev, source_name: value }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon>business</Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <Autocomplete
                  options={getFacilityOptions()}
                  getOptionLabel={(option) => option.name}
                  value={
                    getFacilityOptions().find(
                      (f) => f.id === batchData.warehouse_branch
                    ) || null
                  }
                  onChange={(_, value) => {
                    setBatchData((prev) => ({
                      ...prev,
                      warehouse_branch: value ? value.id : "",
                    }));
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography>{option.name}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: "action.hover",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {option.details}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Facility"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon>location_on</Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  loading={isLoadingFacilities}
                />
              )}
            </Grid>

            {/* Batch Details Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.primary",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                <Icon fontSize="small">inventory</Icon>
                Batch Details
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Batch Number"
                value={batchData.batch_number}
                onChange={(e) => {
                  setBatchData((prev) => ({
                    ...prev,
                    batch_number: e.target.value,
                  }));
                  if (useAutoBatchNumber) setUseAutoBatchNumber(false);
                }}
                required
                InputProps={{
                  endAdornment: useAutoBatchNumber ? (
                    <InputAdornment position="end">
                      <Tooltip title="This is an auto-generated batch ID">
                        <Icon color="action">info</Icon>
                      </Tooltip>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <Tooltip title="Reset to auto-generated batch ID">
                        <IconButton
                          size="small"
                          onClick={handleResetBatchNumber}
                          color="primary"
                        >
                          <Icon>refresh</Icon>
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand Name"
                value={batchData.brand_name || ""}
                onChange={handleChange("brand_name")}
                placeholder="Optional brand name"
              />
            </Grid>

            {/* Quantity and Cost Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.primary",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                <Icon fontSize="small">payments</Icon>
                Quantity & Cost
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={batchData.quantity ?? ""}
                onChange={handleChange("quantity")}
                required
                placeholder="0"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{unit}</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost per Unit"
                type="number"
                value={batchData.cost_per_unit ?? ""}
                onChange={handleChange("cost_per_unit")}
                required
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currency || "$"}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">/ {unit}</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Cost"
                type="number"
                value={batchData.total_cost ?? ""}
                onChange={handleChange("total_cost")}
                required
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currency || "$"}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Storage Place */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={getFacilityOptions()}
                getOptionLabel={(option) => option.name}
                value={
                  getFacilityOptions().find(
                    (f) => f.id === batchData.storage_place
                  ) || null
                }
                onChange={(_, value) => {
                  setBatchData((prev) => ({
                    ...prev,
                    storage_place: value ? value.id : "",
                  }));
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography>{option.name}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: "action.hover",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {option.details}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Storage Place"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>warehouse</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                loading={isLoadingFacilities}
              />
            </Grid>

            {/* Dates Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.primary",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                <Icon fontSize="small">event</Icon>
                Dates
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <AppDatePicker
                label="Receive Date"
                value={
                  batchData.receive_date ? dayjs(batchData.receive_date) : null
                }
                onChange={(date: any) => {
                  if (date) {
                    setBatchData((prev: Batch) => ({
                      ...prev,
                      receive_date: date.valueOf(),
                    }));
                  }
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <AppDatePicker
                label="Expiry Date"
                value={
                  batchData.expiry_date ? dayjs(batchData.expiry_date) : null
                }
                onChange={(date: any) => {
                  if (date) {
                    setBatchData((prev: Batch) => ({
                      ...prev,
                      expiry_date: date.valueOf(),
                    }));
                  }
                }}
                fullWidth
              />
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.primary",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                <Icon fontSize="small">notes</Icon>
                Additional Notes
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={batchData.notes || ""}
                onChange={handleChange("notes")}
                multiline
                rows={2}
                placeholder="Add any additional information about this batch"
              />
            </Grid>
          </Grid>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            startIcon={<Icon>close</Icon>}
            sx={{
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              color: "#0891B2",
              fontFamily: "Montserrat",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#0891B2",
                bgcolor: (theme) => alpha("#0891B2", 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSave}
            loading={loading}
            variant="contained"
            startIcon={<Icon>save</Icon>}
            sx={{
              bgcolor: "#0891B2",
              fontFamily: "Montserrat",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            {selectedBatch ? "Update" : "Add"} Batch
          </LoadingButton>
        </StyledDialogActions>
      </Dialog>
    </Box>
  );
};
